import React, { useState } from "react";

// Helpers
import cloneDeep from "lodash.clonedeep";
import OutsideAlerter from "../../helpers/OutsideAlerter";
import { ScrollEnable } from "../../helpers/ScrollLock";

// Custom components
import {
  SquareIconUtilityButton,
  SquareXButton,
  TextUtilityButton,
} from "../Buttons/Buttons";
import { IconValueSlider } from "../Sliders/Sliders";

// Icons
import {
  ArrowPathIcon,
  CubeIcon,
  CubeTransparentIcon,
  LockClosedIcon,
  LockOpenIcon,
} from "@heroicons/react/24/outline";

// Redux
import { useSelector } from "react-redux";
import { selectMenuOpen } from "../../redux/features/applicationState/applicationStateSlice";

function ComparisonModal(props) {
  const {
    currentComparisonIndex,
    comparisonItems,
    setComparisonItems,
    currentMenu,
    setCurrentMenu,
    touchControls,
    setTouchControls,
    exitFunction,
    setValue,
  } = props;

  const [splitButtonColor, setSplitButtonColor] = useState("bg-cyan-100");
  const [overlayButtonColor, setOverlayButtonColor] =
    useState("bg-neutral-100");

  const menuOpenStatus = useSelector(selectMenuOpen);
  const pairData = comparisonItems[currentComparisonIndex];

  const handleLock = () => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    if (currentMenu === "split") {
      updatedComparisonItems[currentComparisonIndex].lockToggleSplit =
        !pairData.lockToggleSplit;
    } else if (currentMenu === "opacity") {
      updatedComparisonItems[currentComparisonIndex].lockToggleOpacity =
        !pairData.lockToggleOpacity;
    }
    setComparisonItems(updatedComparisonItems);
  };

  const handleTouchControls = () => {
    const updatedTouchControls = cloneDeep(touchControls);
    if (currentMenu === "split") {
      updatedTouchControls.split = !touchControls.split;
    } else if (currentMenu === "opacity") {
      updatedTouchControls.opacity = !touchControls.opacity;
    }
    setTouchControls(updatedTouchControls);
  };

  const handleReset = () => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    if (currentMenu === "split") {
      updatedComparisonItems[currentComparisonIndex].imageZoomSplit = 100;
      updatedComparisonItems[currentComparisonIndex].positionSplit = {
        x: 0,
        y: 0,
      };
    } else if (currentMenu === "opacity") {
      updatedComparisonItems[currentComparisonIndex].imageZoomOpacity = 100;
      updatedComparisonItems[currentComparisonIndex].positionOpacity = {
        x: 0,
        y: 0,
      };
    }

    setComparisonItems(updatedComparisonItems);
  };

  const handleZoom = (event) => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    if (currentMenu === "split") {
      updatedComparisonItems[currentComparisonIndex].imageZoomSplit =
        event.target.value;
    } else if (currentMenu === "opacity") {
      updatedComparisonItems[currentComparisonIndex].imageZoomOpacity =
        event.target.value;
    }
    setComparisonItems(updatedComparisonItems);
  };

  const handleExit = () => {
    ScrollEnable();
    exitFunction(setValue);
  };

  return (
    <div
      className={`fixed z-[1] top-0 right-0 bottom-0 left-0 Xbg-red-500
                  flex flex-col items-center justify-center
                  bg-neutral-400 bg-opacity-25 backdrop-blur-sm
                  pt-12 md:pt-0
                  ${
                    menuOpenStatus
                      ? "md:pl-[6.5rem] ip11l:pl-[25.5rem]"
                      : "md:pl-[6.5rem]"
                  }
                `}
    >
      <div className="flex w-full h-full Xp-2 Xbg-pink-500">
        <OutsideAlerter onOutsideClick={handleExit}>
          <div className="flex flex-col items-center justify-start h-full w-full p-4 md:rounded-l-3xl bg-white">
            <div className="flex flex-row items-center justify-center w-full">
              <div className="flex flex-[1] flex-row items-center justify-center w-full">
                <div className="flex flex-1 flex-row w-full">
                  <TextUtilityButton
                    prompt={"Split"}
                    actionFunction={() => {
                      setCurrentMenu("split");
                      setOverlayButtonColor("bg-neutral-100");
                      setSplitButtonColor("bg-cyan-100");
                    }}
                    customFont="text-lg font-medium text-neutral-700"
                    customPadding="px-3 py-0.5"
                    color={splitButtonColor}
                    fullWidth={true}
                  />
                </div>
                <div className="px-2" />
                <div className="hidden md:flex flex-1 flex-row w-full">
                  <TextUtilityButton
                    prompt={"Overlay"}
                    actionFunction={() => {
                      setCurrentMenu("opacity");
                      setOverlayButtonColor("bg-cyan-100");
                      setSplitButtonColor("bg-neutral-100");
                    }}
                    customFont="text-lg font-medium text-neutral-700"
                    customPadding="px-3 py-0.5"
                    color={overlayButtonColor}
                    fullWidth={true}
                  />
                </div>
                <div className="px-2" />

                <SquareIconUtilityButton
                  Icon={ArrowPathIcon}
                  iconSize="h-6 w-6"
                  activeColor={"active:bg-neutral-200"}
                  actionFunction={handleReset}
                />

                <div className="px-2" />

                <SquareIconUtilityButton
                  Icon={
                    currentMenu === "split"
                      ? pairData.lockToggleSplit
                        ? LockClosedIcon
                        : LockOpenIcon
                      : pairData.lockToggleOpacity
                      ? LockClosedIcon
                      : LockOpenIcon
                  }
                  iconSize="h-6 w-6"
                  activeColor={"active:bg-neutral-200"}
                  actionFunction={handleLock}
                />

                <div className="px-2" />

                <SquareIconUtilityButton
                  Icon={
                    currentMenu === "split"
                      ? touchControls.split
                        ? CubeTransparentIcon
                        : CubeIcon
                      : touchControls.opacity
                      ? CubeTransparentIcon
                      : CubeIcon
                  }
                  iconSize="h-6 w-6"
                  activeColor={"active:bg-neutral-200"}
                  actionFunction={handleTouchControls}
                />
              </div>

              <div className="px-2" />

              <div className="flex flex-1 flex-row items-center justify-center w-full">
                <IconValueSlider
                  sliderValue={
                    currentMenu === "split"
                      ? pairData.imageZoomSplit
                      : pairData.imageZoomOpacity
                  }
                  sliderMin={10}
                  sliderMax={1000}
                  sliderStep={0.001}
                  onChangeFunction={handleZoom}
                  showIcon={false}
                  showValue={false}
                />

                <div className="px-2" />

                <div>
                  <SquareXButton actionFunction={handleExit} />
                </div>
              </div>
            </div>
            <div
              className="flex flex-col w-full Xbg-red-500"
              style={{ height: "calc(100% - 2.5rem)" }}
            >
              {props.children}
            </div>
          </div>
        </OutsideAlerter>
      </div>
    </div>
  );
}

export default ComparisonModal;
